<template>
  <div class="page">
    <main class="content">
      <section class="changelog-section">
        <div class="changelog-header">
          <h1 class="changelog-title">Changelog</h1>
          <p class="changelog-subtitle">Browse every Partycord update in one place.</p>

          <div class="search-container">
            <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              v-model="searchQuery"
              class="search-input"
              type="text"
              placeholder="Search changelogs..."
              aria-label="Search changelogs"
            >
            <button
              v-if="searchQuery"
              class="search-clear"
              @click="searchQuery = ''"
              aria-label="Clear changelog search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <p class="results-info" v-if="searchQuery">
          {{ filteredChangelogs.length }} update{{ filteredChangelogs.length === 1 ? '' : 's' }} found for "{{ searchQuery }}"
        </p>

        <div class="changelog-list" v-if="filteredChangelogs.length">
          <article v-for="entry in filteredChangelogs" :key="`${entry.version}-${entry.date}`" class="changelog-card">
            <header class="entry-header">
              <div>
                <p class="entry-version">{{ entry.version }}</p>
                <h2 class="entry-title" v-html="renderInlineMarkdown(entry.title)"></h2>
              </div>
              <time class="entry-date" :datetime="entry.date">{{ formatDate(entry.date) }}</time>
            </header>

            <div class="entry-summary markdown-body" v-html="renderMarkdown(entry.summary)"></div>

            <div class="entry-tags" v-if="entry.tags && entry.tags.length">
              <span v-for="tag in entry.tags" :key="`${entry.version}-${tag}`" class="entry-tag">{{ tag }}</span>
            </div>

            <div class="change-columns">
              <section v-if="entry.changes.added && entry.changes.added.length" class="change-group">
                <h3 class="change-heading added">Added</h3>
                <ul>
                  <li v-for="(item, index) in entry.changes.added" :key="`added-${entry.version}-${index}`">
                    <span class="markdown-inline" v-html="renderInlineMarkdown(item)"></span>
                  </li>
                </ul>
              </section>

              <section v-if="entry.changes.changed && entry.changes.changed.length" class="change-group">
                <h3 class="change-heading changed">Changed</h3>
                <ul>
                  <li v-for="(item, index) in entry.changes.changed" :key="`changed-${entry.version}-${index}`">
                    <span class="markdown-inline" v-html="renderInlineMarkdown(item)"></span>
                  </li>
                </ul>
              </section>

              <section v-if="entry.changes.fixed && entry.changes.fixed.length" class="change-group">
                <h3 class="change-heading fixed">Fixed</h3>
                <ul>
                  <li v-for="(item, index) in entry.changes.fixed" :key="`fixed-${entry.version}-${index}`">
                    <span class="markdown-inline" v-html="renderInlineMarkdown(item)"></span>
                  </li>
                </ul>
              </section>
            </div>
          </article>
        </div>

        <div class="no-results" v-else>
          <p>No changelog entries matched your search.</p>
          <button class="clear-search-btn" @click="searchQuery = ''">Clear search</button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import changelogs from '../data/changelogs.json';
import { renderInlineMarkdown, renderMarkdown } from '../utils/markdown';

export default {
  name: 'ChangelogPage',
  data() {
    return {
      searchQuery: '',
      changelogs
    };
  },
  computed: {
    sortedChangelogs() {
      return [...this.changelogs].sort((a, b) => this.parseDate(b.date) - this.parseDate(a.date));
    },
    filteredChangelogs() {
      const query = this.searchQuery.toLowerCase().trim();

      if (!query) {
        return this.sortedChangelogs;
      }

      return this.sortedChangelogs.filter((entry) => {
        const searchable = [
          entry.version,
          entry.date,
          entry.title,
          entry.summary,
          ...(entry.tags || []),
          ...(entry.changes?.added || []),
          ...(entry.changes?.changed || []),
          ...(entry.changes?.fixed || [])
        ]
          .join(' ')
          .toLowerCase();

        return searchable.includes(query);
      });
    }
  },
  methods: {
    renderInlineMarkdown,
    renderMarkdown,
    parseDate(rawDate) {
      // Parse ISO-like dates as local dates to avoid timezone day shifting.
      const isoMatch = /^\d{4}-\d{1,2}-\d{1,2}$/.exec(rawDate);

      if (isoMatch) {
        const [year, month, day] = rawDate.split('-').map(Number);
        return new Date(year, month - 1, day);
      }

      return new Date(rawDate);
    },
    formatDate(rawDate) {
      const date = this.parseDate(rawDate);

      if (Number.isNaN(date.getTime())) {
        return rawDate;
      }

      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.changelog-section {
  width: 100%;
  max-width: var(--container-max-width);
}

.changelog-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.changelog-title {
  font-size: clamp(2.5rem, 6vw, var(--font-size-display));
  margin-bottom: var(--spacing-sm);
  background: linear-gradient(135deg, #00b7ff, #fdbb2d);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.changelog-subtitle {
  color: var(--color-text-secondary);
  margin: 0 auto var(--spacing-md) auto;
  font-size: var(--font-size-md);
}

.search-container {
  position: relative;
  max-width: 560px;
  margin: 0 auto;
}

.search-input {
  width: 100%;
  border: 2px solid transparent;
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-md) var(--spacing-md) var(--spacing-md) 3.2rem;
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-accent-discord);
  box-shadow: var(--shadow-lg), 0 0 20px rgba(88, 101, 242, 0.2);
}

.search-icon {
  width: 20px;
  height: 20px;
  position: absolute;
  top: 50%;
  left: 1.1rem;
  transform: translateY(-50%);
  color: var(--color-text-muted);
}

.search-clear {
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  padding: 0.25rem;
  cursor: pointer;
}

.search-clear svg {
  width: 18px;
  height: 18px;
}

.results-info {
  text-align: center;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
}

.changelog-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.changelog-card {
  text-align: left;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
  box-shadow: var(--shadow-md);
}

.entry-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
}

.entry-version {
  color: #22c1c3;
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.3rem;
}

.entry-title {
  margin: 0;
  font-size: 1.4rem;
  color: var(--color-text-primary);
}

.entry-date {
  color: var(--color-text-muted);
  font-size: var(--font-size-xs);
}

.entry-summary {
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-sm);
}

.markdown-body {
  color: var(--color-text-secondary);
  display: grid;
  gap: 0.55rem;
}

.markdown-body :deep(p) {
  margin: 0;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  margin: 0;
  padding-left: 1.15rem;
}

.markdown-body :deep(a),
.markdown-inline :deep(a) {
  color: #7fe7e8;
  text-decoration: underline;
}

.markdown-body :deep(code),
.markdown-inline :deep(code) {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0.35rem;
  padding: 0.12rem 0.35rem;
  font-family: var(--font-mono);
  font-size: 0.92em;
}

.entry-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: var(--spacing-sm);
}

.entry-tag {
  font-size: 0.78rem;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: var(--color-text-secondary);
  background: rgba(255, 255, 255, 0.05);
}

.change-columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-sm);
}

.change-group {
  background: rgba(255, 255, 255, 0.03);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-sm);
}

.change-heading {
  font-size: var(--font-size-sm);
  margin-bottom: 0.4rem;
}

.change-heading.added {
  color: #52d273;
}

.change-heading.changed {
  color: #fdbb2d;
}

.change-heading.fixed {
  color: #ff7f7f;
}

.change-group ul {
  margin: 0;
  padding-left: 1.1rem;
  color: var(--color-text-secondary);
}

.change-group li {
  margin-bottom: 0.3rem;
  font-size: 0.95rem;
}

.change-group li:last-child {
  margin-bottom: 0;
}

.no-results {
  text-align: center;
  padding: var(--spacing-xl) var(--spacing-md);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--border-radius-lg);
}

.clear-search-btn {
  margin-top: var(--spacing-sm);
  border: none;
  border-radius: var(--border-radius-md);
  padding: 0.6rem 1rem;
  font-weight: 600;
  color: #fff;
  background: var(--color-accent-discord);
  cursor: pointer;
}

.clear-search-btn:hover {
  background: var(--color-accent-discord-hover);
}

@media (max-width: 768px) {
  .entry-title {
    font-size: 1.2rem;
  }
}
</style>